<script setup lang="ts">
import { ref, useId } from 'vue';
import { X } from 'lucide-vue-next';
import { useOverlay } from './overlay';

/**
 * The edit surface. Right side, overlaying the content, with the list still
 * visible behind it — the context is load-bearing rather than decorative:
 * granting someone `core:admin` while the existing admins stay on screen is a
 * different decision from granting it blind.
 *
 * Dismissed by its backdrop, unlike a modal. Nothing here is irreversible
 * enough for a stray click to matter.
 */
const props = defineProps<{ open: boolean; title: string; description?: string | undefined }>();
const emit = defineEmits<{ close: [] }>();

const panel = ref<HTMLElement>();
const titleId = useId();

const { onBackdropClick } = useOverlay({
	open: () => props.open,
	container: panel,
	onClose: () => {
		emit('close');
	},
	closeOnBackdrop: true,
});
</script>

<template>
	<Teleport to="body">
		<div
			v-if="open"
			class="fixed inset-0 z-40 flex justify-end bg-black/40"
			@click="onBackdropClick"
		>
			<div
				ref="panel"
				role="dialog"
				aria-modal="true"
				:aria-labelledby="titleId"
				tabindex="-1"
				class="bg-surface border-border flex h-full w-full max-w-md flex-col border-l shadow-xl focus:outline-none"
			>
				<header
					class="border-border flex items-start justify-between gap-4 border-b px-5 py-4"
				>
					<div>
						<h2 :id="titleId" class="font-medium">{{ title }}</h2>
						<p v-if="description" class="text-muted mt-0.5 text-sm">
							{{ description }}
						</p>
					</div>
					<button
						type="button"
						class="text-muted hover:text-fg rounded p-1"
						aria-label="Close"
						@click="emit('close')"
					>
						<X :size="18" aria-hidden="true" />
					</button>
				</header>

				<div class="flex-1 overflow-y-auto px-5 py-4"><slot /></div>

				<footer
					v-if="$slots.footer"
					class="border-border flex justify-end gap-2 border-t px-5 py-4"
				>
					<slot name="footer" />
				</footer>
			</div>
		</div>
	</Teleport>
</template>
